import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { AnalyticsEnvelope, UtilizationRow, AnalyticsViewProps } from "./types";

type UtilizationData = AnalyticsEnvelope & {
  series: UtilizationRow[];
  expected_seconds_per_day?: number;
};

export function UtilizationView({
  data,
  loading,
  error,
}: AnalyticsViewProps<UtilizationData>) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const [asTable, setAsTable] = useState(false);
  const rows = (data?.series ?? []).map((r) => ({
    period: r.period,
    utilization: Math.round(r.utilization * 100),
  }));
  const toggle = (
    <button
      type="button"
      className="text-[12px] text-muted-foreground underline"
      onClick={() => setAsTable((v) => !v)}
    >
      {t("homework.analytics.viewAsTable")}
    </button>
  );
  return (
    <Panel
      title={t("homework.analytics.utilization.title")}
      action={rows.length ? toggle : undefined}
    >
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !rows.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : asTable ? (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left">{t("homework.analytics.attendance.date")}</th>
              <th className="text-right">{t("homework.analytics.utilization.title")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.period}>
                <td>{r.period}</td>
                <td className="text-right tabular-nums">{r.utilization}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div role="img" aria-label={t("homework.analytics.utilization.title")}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="period" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
            <YAxis
              stroke={colors.axis}
              tick={{ fontSize: 12, fill: colors.axis }}
              width={36}
              domain={[0, 100]}
              unit="%"
            />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="utilization"
              stroke={colors.series(0)}
              strokeWidth={2}
              dot={{ r: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        </div>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
